import { AI_PROFILES, chooseShot, type AiProfile, type Difficulty } from "@scb/ai";
import {
  ClientMessage,
  ServerMessage,
  validateShotInput,
  type ClientMessageType,
  type FinalSequenceOfferPayload,
  type MatchEndedPayload,
  type RejectedPayload,
  type RoundEndedPayload,
  type ShotInput,
  type LaunchSide,
  type ShotResultPayload,
  type TeamId,
} from "@scb/protocol";
import {
  DEFAULT_RULES,
  acceptsShots,
  applyShot,
  bankPoints,
  continueFinalSequence,
  createMatchState,
  endRound,
  mixSeed,
  launchSideFor,
  startRound,
  type MatchState,
  type RoundEndCause,
  type RulesConfig,
} from "@scb/rules";
import {
  DEFAULT_TABLE,
  isLegalLaunchPoint,
  isLegalLaunchPointFor,
  type TableSpec,
} from "@scb/sim";
import { replayDurationMs } from "./replay";
import {
  LOCAL_PRACTICE_SUMMARY,
  type BallView,
  type BattleView,
  type GameSession,
  type PlayerView,
  type PracticeSummary,
  type SessionMode,
} from "./session";

/** 연습 모드 한 세트의 공 개수 */
export const PRACTICE_BALLS = 12;

/** AI가 "생각하는" 시간. 즉시 쏘면 사람이 못 따라간다. */
const AI_THINK_MS = 600;
/** 리플레이가 끝난 뒤 다음 동작까지의 여유 */
const REPLAY_PAD_MS = 450;
/** 라운드 사이 대기 */
const INTERMISSION_MS = 4_000;

export interface LocalSessionOptions {
  mode: "practice" | "ai";
  name: string;
  difficulty: Difficulty;
  seed: number;
  /**
   * 연습 모드 한 세트의 공 개수. 생략하면 PRACTICE_BALLS(12).
   * 큰 값(9999)을 주면 사실상 무제한 — 세트가 끝나지 않고 계속 던진다.
   */
  practiceBalls?: number;
  /** 안착 공이 빠지면 점수 차감 (선택 규칙). 온라인 방 옵션과 같은 의미. 기본 false. */
  knockoutPenalty?: boolean;
}

const DIFFICULTY_LABEL: Record<Difficulty, string> = {
  easy: "하",
  normal: "중",
  hard: "상",
};

/**
 * 서버 없이 로컬에서 돌아가는 세션.
 *
 * - `practice`: 상대 없이 12구를 던지며 점수를 확인한다. 턴이 넘어가지 않고 시간 제한도 없다.
 * - `ai`: `@scb/ai` 봇과 정식 규칙(2라운드 + 연장)으로 대전한다.
 *
 * 규칙·물리는 서버와 **완전히 같은 코드**(`@scb/rules`, `@scb/sim`)를 쓴다.
 * 그래서 여기서 익힌 감각이 온라인에서 그대로 통한다.
 */
export class LocalSession implements GameSession {
  readonly kind: SessionMode;
  readonly sessionId = "you";
  readonly state: BattleView;

  private readonly table: TableSpec = DEFAULT_TABLE;
  private readonly config: RulesConfig;
  private readonly profile: AiProfile;
  private engine: MatchState;

  private msgHandlers = new Map<string, ((msg: never) => void)[]>();
  private stateHandlers: ((state: BattleView) => void)[] = [];
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private loop: ReturnType<typeof setInterval> | null = null;
  private closed = false;

  private nextPhaseAt = 0;
  private pendingOvertime = false;
  private shots = 0;
  private bestShot = 0;

  /**
   * 연습 모드에서 앉을 자리. 대전은 라운드에서 자리가 유도되지만(`launchSideFor`)
   * 연습은 1라운드뿐이라 교대가 없다. 그래서 직접 고르게 하고, 양쪽 다 연습할 수
   * 있도록 세트 중에도 바꿀 수 있다.
   */
  private practiceSideValue: LaunchSide = "left";

  constructor(options: LocalSessionOptions) {
    this.kind = options.mode;
    this.profile = AI_PROFILES[options.difficulty];

    // 선택 규칙은 온라인 방과 같은 의미로 로컬에도 적용한다 —
    // 여기서 익힌 감각이 온라인에서 그대로 통해야 한다.
    const knockoutPenalty = options.knockoutPenalty ?? false;
    this.config =
      options.mode === "practice"
        ? {
            ...DEFAULT_RULES,
            knockoutPenalty,
            roundCount: 1,
            ballsPerTeam: options.practiceBalls ?? PRACTICE_BALLS,
            // 연습에는 시간 제한이 없다.
            roundDurationMs: 365 * 24 * 3600 * 1000,
            // 대전과 같은 자리에서 던져야 여기서 익힌 감각이 그대로 통한다.
            // 다만 라운드 교대가 없으므로 자리는 `practiceSide`로 직접 고른다.
            fixedLaunchSides: true,
            // 12구를 다 던져보는 게 목적이므로 만관으로 끊지 않는다.
            endRoundOnGrandSlam: false,
          }
        : { ...DEFAULT_RULES, knockoutPenalty };

    this.engine = createMatchState({
      table: this.table,
      config: this.config,
      teamSize: 1,
      seed: options.seed,
    });

    const players = new Map<string, PlayerView>();
    players.set(this.sessionId, {
      sessionId: this.sessionId,
      name: options.name,
      steamId: "",
      team: 0,
      slot: 0,
      connected: true,
      ready: true,
    });
    if (options.mode === "ai") {
      players.set("ai", {
        sessionId: "ai",
        name: `AI (난이도 ${DIFFICULTY_LABEL[options.difficulty]})`,
        steamId: "",
        team: 1,
        slot: 0,
        connected: true,
        ready: true,
      });
    }

    this.state = {
      format: "1v1",
      phase: "lobby",
      round: 0,
      phaseEndsAt: 0,
      turnEndsAt: 0,
      turnTeam: 0,
      currentShooter: "",
      finalSequenceTeam: -1,
      winner: -1,
      teams: [
        { score: 0, ballsRemaining: 0 },
        { score: 0, ballsRemaining: 0 },
      ],
      players,
      balls: [],
    };
  }

  // ── GameSession ────────────────────────────────────────

  onStateChange(cb: (state: BattleView) => void): void {
    this.stateHandlers.push(cb);
  }

  onMessage<T>(type: string, cb: (msg: T) => void): void {
    const list = this.msgHandlers.get(type) ?? [];
    list.push(cb as (msg: never) => void);
    this.msgHandlers.set(type, list);
  }

  send(type: string, payload?: unknown): void {
    if (this.closed) return;
    switch (type) {
      case ClientMessage.Shoot:
        this.handleShoot(payload);
        break;
      case ClientMessage.BankPoints:
        this.handleBank();
        break;
      case ClientMessage.ContinueFinalSequence:
        this.handleContinue();
        break;
      default:
        // Ready / ChangeTeam / Chat 은 로컬 모드에서 의미가 없다.
        break;
    }
  }

  async leave(): Promise<void> {
    this.closed = true;
    this.clearTimers();
    if (this.loop !== null) {
      clearInterval(this.loop);
      this.loop = null;
    }
  }

  // ── 로컬 전용 API ──────────────────────────────────────

  /** 세션을 시작한다 (온라인의 "준비 → 카운트다운"에 해당). */
  start(): void {
    this.beginRound(1);
    // 연습 모드는 타이머가 없으므로 루프도 필요 없다.
    if (this.kind === "ai") {
      this.loop = setInterval(() => this.tick(), 500);
    }
  }

  /** 연습 모드: 테이블과 점수를 비우고 새 세트를 시작한다. */
  reset(): void {
    this.clearTimers();
    this.engine.teams[0].score = 0;
    this.engine.teams[1].score = 0;
    this.shots = 0;
    this.bestShot = 0;
    this.beginRound(1);
  }

  get practiceStats(): PracticeSummary {
    return { shots: this.shots, total: this.engine.teams[0].score, best: this.bestShot };
  }

  /** 연습 모드에서 지금 앉아 있는 자리. */
  get practiceSide(): LaunchSide {
    return this.practiceSideValue;
  }

  /** 연습 중 자리를 바꾼다. 좌우가 대칭이라 던지던 감각을 뒤집어 확인할 수 있다. */
  swapPracticeSide(): LaunchSide {
    this.practiceSideValue = this.practiceSideValue === "left" ? "right" : "left";
    this.sync();
    return this.practiceSideValue;
  }

  /**
   * 팀이 이번에 쓰는 자리.
   *
   * 연습은 라운드 교대가 없어 `practiceSide`를 그대로 쓰고, 대전은 라운드에서
   * 유도한다 — 서버와 **같은** `launchSideFor`라 판정이 갈라지지 않는다.
   */
  private sideFor(team: TeamId): LaunchSide {
    if (this.kind !== "practice") return launchSideFor(this.engine.round, team);
    if (team === 0) return this.practiceSideValue;
    return this.practiceSideValue === "left" ? "right" : "left";
  }

  // ── 진행 ───────────────────────────────────────────────

  private beginRound(round: number): void {
    startRound(this.engine, {
      table: this.table,
      config: this.config,
      round,
      nowMs: Date.now(),
      teamSize: 1,
    });
    // 연습은 언제나 플레이어 차례다 (startRound는 라운드마다 선공을 바꾼다).
    if (this.kind === "practice") this.engine.turnTeam = 0;
    this.pendingOvertime = false;

    this.emitTurn();
    if (this.engine.turnTeam === 1) this.scheduleAiShot();
  }

  private tick(): void {
    if (this.closed) return;
    const now = Date.now();
    const phase = this.engine.phase;

    if ((phase === "round" || phase === "overtime") && now >= this.engine.phaseEndsAtMs) {
      this.concludeRound(now);
      return;
    }
    if (phase === "intermission" && now >= this.nextPhaseAt) {
      this.beginRound(
        this.pendingOvertime ? this.config.roundCount + 1 : this.engine.round + 1,
      );
    }
  }

  private handleShoot(payload: unknown): void {
    if (!acceptsShots(this.engine.phase)) {
      return this.reject(ClientMessage.Shoot, "wrong_phase");
    }
    if (this.engine.finalSequenceTeam !== null) {
      return this.reject(ClientMessage.Shoot, "wrong_phase", "Final Sequence 선택을 먼저 해주세요");
    }
    if (this.engine.turnTeam !== 0) {
      return this.reject(ClientMessage.Shoot, "not_your_turn");
    }
    if (!validateShotInput(payload)) {
      return this.reject(ClientMessage.Shoot, "invalid_payload");
    }
    const shot = payload as ShotInput;
    if (!isLegalLaunchPoint(this.table, shot.x)) {
      return this.reject(ClientMessage.Shoot, "play_line", "발사 지점이 테이블 밖입니다");
    }
    // 서버와 같은 자리 규칙을 그대로 건다 — AI 대전에서 익힌 감각이 온라인에서 통해야 한다.
    if (this.config.fixedLaunchSides) {
      const side = this.sideFor(0);
      if (!isLegalLaunchPointFor(this.table, side, shot.x)) {
        return this.reject(
          ClientMessage.Shoot,
          "play_line",
          `이번 라운드는 ${side === "left" ? "왼쪽" : "오른쪽"} 자리 고정입니다`,
        );
      }
    }
    if (this.engine.teams[0].ballsRemaining <= 0) {
      return this.reject(ClientMessage.Shoot, "out_of_balls");
    }
    this.resolveShot(0, shot);
  }

  private resolveShot(team: TeamId, shot: ShotInput): void {
    const shotId = this.engine.shotId;
    const outcome = applyShot({
      state: this.engine,
      table: this.table,
      config: this.config,
      team,
      shot,
      sampleHz: 30,
    });

    if (team === 0) {
      this.shots++;
      this.bestShot = Math.max(this.bestShot, outcome.shooterScore.total);
    }

    // 연습 모드는 상대가 없으므로 항상 내 차례로 되돌린다.
    if (this.kind === "practice") this.engine.turnTeam = 0;

    this.sync();
    this.emit(ServerMessage.ShotResult, {
      shotId,
      by: { sessionId: team === 0 ? this.sessionId : "ai", team, slot: 0 },
      input: shot,
      replay: {
        seed: outcome.seed,
        sampleHz: outcome.sim.sampleHz,
        frames: outcome.sim.frames,
      },
      score: outcome.shooterScore,
      totals: outcome.totals,
      knockouts: outcome.knockouts,
      turnEnded: outcome.turnEnded,
      turnEndReason: outcome.turnEndReason,
      foul: null,
    } satisfies ShotResultPayload);

    // 화면 재생이 끝나는 시점에 맞춰 다음 동작을 잡는다 (긴 샷은 배속으로 재생된다).
    const replayMs =
      replayDurationMs(outcome.sim.frames.length, outcome.sim.sampleHz) + REPLAY_PAD_MS;

    this.after(replayMs, () => {
      if (this.kind === "practice") {
        if (this.engine.teams[0].ballsRemaining <= 0) this.finishPracticeSet();
        else this.emitTurn();
        return;
      }

      if (this.engine.phase === "ended") {
        this.emitMatchEnded();
        return;
      }
      if (outcome.finalSequenceOffered !== null) {
        this.offerFinalSequence(outcome.finalSequenceOffered);
        return;
      }
      if (outcome.roundOver) {
        this.concludeRound(Date.now(), {
          reason: outcome.roundOverReason ?? "out_of_balls",
          grandSlam: outcome.grandSlam,
        });
        return;
      }

      this.emitTurn();
      if (this.engine.turnTeam === 1) this.scheduleAiShot();
    });
  }

  private scheduleAiShot(): void {
    this.after(AI_THINK_MS, () => {
      if (this.closed || this.kind !== "ai") return;
      if (this.engine.turnTeam !== 1) return;
      if (!acceptsShots(this.engine.phase)) return;
      if (this.engine.teams[1].ballsRemaining <= 0) return;

      // applyShot이 쓸 시드/공 id를 미리 계산해서 AI에게 넘긴다.
      // 결정론이라 AI가 평가한 결과와 실제 결과가 정확히 일치한다.
      const seed = mixSeed(this.engine.seed, this.engine.shotId);
      const shot = chooseShot({
        table: this.table,
        balls: this.engine.balls,
        team: 1,
        config: this.config,
        seed,
        searchSeed: (seed ^ 0x5bf03635) | 0,
        ballId: this.engine.nextBallId,
        profile: this.profile,
        plateAccum: [this.engine.teams[0].plateAccum, this.engine.teams[1].plateAccum],
        // AI도 자기 자리를 지켜야 한다. 안 그러면 사람만 반쪽 테이블을 쓴다.
        launchSide: this.config.fixedLaunchSides ? this.sideFor(1) : undefined,
      });

      this.resolveShot(1, shot);
    });
  }

  // ── Final Sequence ─────────────────────────────────────

  private offerFinalSequence(team: TeamId): void {
    this.sync();

    if (team === 1) {
      // AI는 항상 계속 진행을 택한다 (남은 공을 던지는 게 기대값이 높다).
      this.after(AI_THINK_MS, () => {
        continueFinalSequence(this.engine);
        this.emitTurn();
        this.scheduleAiShot();
      });
      return;
    }

    this.emit(ServerMessage.FinalSequenceOffer, {
      team,
      ballsRemaining: this.engine.teams[team].ballsRemaining,
      bankValue: this.engine.teams[team].ballsRemaining * this.config.leftoverBallPoints,
      decideWithinMs: this.config.finalSequenceDecideMs,
    } satisfies FinalSequenceOfferPayload);
  }

  private handleBank(): void {
    if (this.engine.finalSequenceTeam !== 0) {
      return this.reject(ClientMessage.BankPoints, "not_allowed");
    }
    bankPoints(this.engine, this.config, 0);
    this.sync();
    this.concludeRound(Date.now(), { reason: "banked" });
  }

  private handleContinue(): void {
    if (this.engine.finalSequenceTeam !== 0) {
      return this.reject(ClientMessage.ContinueFinalSequence, "not_allowed");
    }
    continueFinalSequence(this.engine);
    this.engine.turnTeam = 0;
    this.emitTurn();
  }

  // ── 종료 ───────────────────────────────────────────────

  private concludeRound(now: number, end: RoundEndCause = { reason: "time_up" }): void {
    const result = endRound(this.engine, this.config, now, end);
    this.sync();

    this.emit(ServerMessage.RoundEnded, {
      round: this.engine.round,
      totals: result.totals,
      leftoverBonus: result.leftoverBonus,
      nextPhase: result.nextPhase,
      reason: result.reason,
      grandSlam: result.grandSlam,
    } satisfies RoundEndedPayload);

    if (result.nextPhase === "ended") {
      this.emitMatchEnded();
      return;
    }

    this.pendingOvertime = result.nextPhase === "overtime";
    this.nextPhaseAt = now + INTERMISSION_MS;
    this.engine.phaseEndsAtMs = this.nextPhaseAt;
    this.sync();
  }

  private emitMatchEnded(): void {
    this.sync();
    this.emit(ServerMessage.MatchEnded, {
      totals: [this.engine.teams[0].score, this.engine.teams[1].score],
      winner: this.engine.winner,
      reason: this.engine.endReason ?? "score",
    } satisfies MatchEndedPayload);
  }

  private finishPracticeSet(): void {
    this.engine.phase = "ended";
    this.sync();
    this.emit(LOCAL_PRACTICE_SUMMARY, this.practiceStats satisfies PracticeSummary);
  }

  // ── 상태 / 이벤트 ──────────────────────────────────────

  private emitTurn(): void {
    this.sync();
    this.emit(ServerMessage.TurnChanged, {
      team: this.engine.turnTeam,
      round: this.engine.round,
      sessionId: this.engine.turnTeam === 0 ? this.sessionId : "ai",
      // 로컬에는 턴 제한시간이 없다.
      turnTimeoutMs: 0,
    });
  }

  private sync(): void {
    const s = this.state;
    s.phase = this.engine.phase;
    s.round = this.engine.round;
    s.phaseEndsAt = this.kind === "practice" ? 0 : this.engine.phaseEndsAtMs;
    s.turnEndsAt = 0;
    s.turnTeam = this.engine.turnTeam;
    s.currentShooter = this.engine.turnTeam === 0 ? this.sessionId : "ai";
    s.finalSequenceTeam = this.engine.finalSequenceTeam ?? -1;
    s.winner = this.engine.winner ?? -1;

    s.teams[0].score = this.engine.teams[0].score;
    s.teams[0].ballsRemaining = this.engine.teams[0].ballsRemaining;
    s.teams[1].score = this.engine.teams[1].score;
    s.teams[1].ballsRemaining = this.engine.teams[1].ballsRemaining;

    const balls: BallView[] = [];
    for (const b of this.engine.balls) {
      if (b.state === "gutter") continue;
      balls.push({
        id: b.id,
        team: b.team,
        x: b.pos.x,
        y: b.pos.y,
        state: b.state,
        astrolabe: b.astrolabe ?? 0,
        slot: b.slot ?? -1,
      });
    }
    s.balls = balls;

    for (const cb of this.stateHandlers) cb(s);
  }

  private emit(type: string, payload: unknown): void {
    const list = this.msgHandlers.get(type);
    if (!list) return;
    for (const cb of list) (cb as (msg: unknown) => void)(payload);
  }

  private reject(
    message: ClientMessageType,
    code: RejectedPayload["code"],
    detail?: string,
  ): void {
    this.emit(ServerMessage.Rejected, { message, code, detail } satisfies RejectedPayload);
  }

  private after(ms: number, fn: () => void): void {
    const id = setTimeout(() => {
      this.timers.delete(id);
      if (!this.closed) fn();
    }, ms);
    this.timers.add(id);
  }

  private clearTimers(): void {
    for (const id of this.timers) clearTimeout(id);
    this.timers.clear();
  }
}
